import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
export interface ColumnConfig {
  field: string;
  title: string;
  type?: string;
  subField?: string;
  arrayItemField?: string;
  tableId?: string;
  width?: number;
  hidden?: boolean;
  sortable?: boolean;
  chipValue?: string[];
}

export interface FilterConfig {
  field: string;
  title: string;
  dataType: 'String' | 'Number' | 'Date' | 'Lookup' | 'SpecialLookup' | 'ProvidedList' | 'Boolean';
  lookupCode?: string;
  tableId?: string;
  operators?: string[];
  listOptions?: { list: any[]; id: string; name: string; };
}

export interface SortConfig {
  field: string;
  sortValue: string | string[];
}

@Injectable({
  providedIn: 'root'
})
export class TalentTableConfigService {
  private readonly _columns: ColumnConfig[] = [
    { field: "employee", title: "Employee", type: "employee", sortable: true, width: 200 },
    { field: "status", subField: "name", title: "Status", type: 'chip', chipValue: ['Active', 'InActive', 'Future']  },
    { field: "title", subField: "text", title: "Title", tableId: "tfi_Title" },
    { field: "preferredName", title: "Preferred Name" },
    { field: "formalName", title: "Formal Name" },
    { field: "maidenName", title: "Maiden Name" },
    { field: "middleName", title: "Middle Name" },
    { field: "age", title: "Age" },
    { field: "gender", subField: "text", title: "Gender" },
    { field: "culturalAffiliation", subField: "text", title: "Cultural Affiliation" },
    { field: "religiousAffiliation", subField: "text", title: "Religious Affiliation" },
    { field: "sexualOrientation", subField: "text", title: "Sexual Orientation" },
    { field: "customClientId", title: "Custom Client ID" },
    { field: "linkedInUrl", title: "LinkedIn URL" },
    { field: "twitterHandle", title: "Twitter Handle" },
    { field: "facebookUrl", title: "Facebook URL" },
    { field: "birthday", title: "Birthday", type: "date" },
    { field: "nextBirthday", title: "Next Birthday", type: "date" },
    { field: "maritalStatus", subField: "text", title: "Marital Status" },
    { field: "subStatus", subField: "text", title: "Sub Status" },
    { field: "streetAddress", title: "Street Address" },
    { field: "city", title: "City" },
    { field: "province", title: "Province" },
    { field: "zip", title: "ZIP" },
    { field: "country", subField: "name", title: "Country" },
    { field: "email", title: "Email" },
    { field: "workPhone", title: "Work Phone" },
    { field: "homePhone", title: "Home Phone" },
    { field: "cellPhone", title: "Cell Phone" },
    { field: "culture", subField: "text", title: "Culture" },
    { field: "positions", title: "Positions", arrayItemField: "name", type: "array" },
    { field: "timezone", subField: "text", title: "Timezone" },
    { field: "workLocations", title: "Work Locations", arrayItemField: "name", type: "array" },
    { field: "managers", title: "Managers", type: "employees" },
    { field: "departments", title: "Departments", arrayItemField: "name", type: "array" },
    { field: "streetAddress2", title: "Street Address 2" },
    { field: "organizationUnits", title: "Organization Units", arrayItemField: "name", type: "array" },
    { field: "municipality", title: "Municipality" },
    { field: "region1", title: "Region 1" },
    { field: "region2", title: "Region 2" },
    { field: "region3", title: "Region 3" },
    { field: "neighbourhood", title: "Neighbourhood" },
    { field: "email2", title: "Email 2" },
    { field: "internetAddress", title: "Internet Address" },
    { field: "socialSecurityNumber", title: "Social Security Number" },
    { field: "identityNumber", title: "Identity Number" },
    { field: "regionOfBirth", title: "Region of Birth" },
    { field: "townOfBirth", title: "Town of Birth" },
    { field: "poBox", title: "PO Box" },
    { field: "userId", title: "User ID" },
    { field: "registeredDisabled", subField: "text", title: "Registered Disabled" },
    { field: "disability", subField: "text", title: "Disability" },
    { field: "veteranStatus", subField: "text", title: "Veteran Status" },
    { field: "equalEmploymentOpportunity", subField: "text", title: "Equal Employment Opportunity" },
    { field: "nationality", subField: "text", title: "Nationality" },
    { field: "countryOfBirth", subField: "name", title: "Country of Birth" },
    { field: "organization", subField: "name", title: "Organization" },
    { field: "username", title: "Username" },
    { field: "preferredPronoun", title: "Preferred Pronoun" },
    { field: "nextNumberId1", title: "Next Number ID 1" }
  ];

  private readonly _filters: FilterConfig[] = [
    { 
      field: "status.id", 
      title: "Status", 
      dataType: "ProvidedList", 
      listOptions: {
        list: [
          {id: 'Active', name: 'Active'}, 
          {id: 'inActive', name: 'Inactive'}, 
          {id: 'Future', name: 'Future'}
        ],
        id: 'id',
        name: 'name'
      } 
    },
    { field: "birthday", title: "Birthday", dataType: "Date" },
    { field: "cellPhone", title: "Cell Phone", dataType: "String", tableId: "tfi_EmpCellPhone" },
    { field: "city", title: "City", dataType: "String" },
    { field: "country.id", title: "Country", dataType: "SpecialLookup", lookupCode: "country" },
    { field: "countryOfBirth.id", title: "Country of Birth", dataType: "Lookup", lookupCode: "COUNTRY" },
    { field: "culturalAffiliation.id", title: "Cultural Affiliation", dataType: "Lookup", lookupCode: "CULTURAL_AFFILIATION" },
    { field: "customClientId", title: "Custom Client ID", dataType: "String", tableId: "tfi_EmployeeId" },
    { field: "disability.id", title: "Disability", dataType: "Lookup", lookupCode: "DISABILITY" },
    { field: "email", title: "Email", dataType: "String", tableId: "tfi_EmpEmail" },
    { field: "equalEmploymentOpportunity.id", title: "Equal Employment Opportunity", dataType: "Lookup", lookupCode: "EQUAL_OPPORTUNITY" },
    { field: "facebookUrl", title: "Facebook URL", dataType: "String", tableId: "tfi_EmpFacebookUrl" },
    { field: "firstName", title: "First Name", dataType: "String", tableId: "tfi_FirstName" },
    { field: "gender.id", title: "Gender", dataType: "Lookup", lookupCode: "GENDER" },
    { field: "homePhone", title: "Home Phone", dataType: "String" },
    { field: "identityNumber", title: "Identity Number", dataType: "String" },
    { field: "internetAddress", title: "Internet Address", dataType: "String" },
    { field: "lastName", title: "Last Name", dataType: "String", tableId: "tfi_LastName" },
    { field: "linkedInUrl", title: "LinkedIn URL", dataType: "String", tableId: "tfi_EmpLinkedInUrl" },
    { field: "maidenName", title: "Maiden Name", dataType: "String" },
    { field: "maritalStatus.id", title: "Marital Status", dataType: "Lookup", lookupCode: "MARITAL_STATUS" },
    { field: "middleName", title: "Middle Name", dataType: "String" },
    { field: "municipality", title: "Municipality", dataType: "String" },
    { field: "nationality.id", title: "Nationality", dataType: "Lookup", lookupCode: "NATIONALITY" },
    { field: "neighbourhood", title: "Neighbourhood", dataType: "String" },
    { field: "nextNumberId1", title: "Next Number ID 1", dataType: "Number" },
    { field: "organization.id", title: "Organization", dataType: "SpecialLookup", lookupCode: "ORGANISATION" },
    { field: "poBox", title: "PO Box", dataType: "String" },
    { field: "preferredName", title: "Preferred Name", dataType: "String", tableId: "tfi_PreferredName" },
    { field: "preferredPronoun", title: "Preferred Pronoun", dataType: "String" },
    { field: "province", title: "Province", dataType: "String" },
    { field: "region1", title: "Region 1", dataType: "String" },
    { field: "region2", title: "Region 2", dataType: "String" },
    { field: "region3", title: "Region 3", dataType: "String" },
    { field: "regionOfBirth", title: "Region of Birth", dataType: "String" },
    { field: "registeredDisabled.id", title: "Registered Disabled", dataType: "Lookup", lookupCode: "REGISTERED_DISABLED" },
    { field: "religiousAffiliation.id", title: "Religious Affiliation", dataType: "Lookup", lookupCode: "RELIGIOUS_AFFILIATION" },
    { field: "sexualOrientation.id", title: "Sexual Orientation", dataType: "Lookup", lookupCode: "SEXUAL_ORIENTATION" },
    { field: "socialSecurityNumber", title: "Social Security Number", dataType: "String" },
    { field: "streetAddress", title: "Street Address", dataType: "String" },
    { field: "streetAddress2", title: "Street Address 2", dataType: "String" },
    { field: "timezone.id", title: "Timezone", dataType: "SpecialLookup", lookupCode: "TIMEZONE", tableId: "tfi_EmpTimezone" },
    { field: "title.id", title: "Title", dataType: "Lookup", lookupCode: "TITLE" },
    { field: "townOfBirth", title: "Town of Birth", dataType: "String" },
    { field: "twitterHandle", title: "Twitter Handle", dataType: "String", tableId: "tfi_EmpTwitterHandle" },
    { field: "username", title: "Username", dataType: "String" },
    { field: "veteranStatus.id", title: "Veteran Status", dataType: "Lookup", lookupCode: "VETERAN_STATUS" },
    { field: "workPhone", title: "Work Phone", dataType: "String", tableId: "tfi_EmpWorkPhone" },
    { field: "workLocation.id", title: "Work Location", dataType: "SpecialLookup", lookupCode: "WORK_LOCATION", tableId: "tfi_EmpWorkLocations" },
    { field: "zip", title: "ZIP", dataType: "String" }
  ];

  private readonly _sorts: SortConfig[] = [
    { field: "employee", sortValue: ["Firstname", "Lastname"] },
    { field: "status", sortValue: "status.name" },
    { field: "preferredName", sortValue: "preferredName" },
    { field: "title", sortValue: "title.text" },
    { field: "formalName", sortValue: "formalName" },
    { field: "maidenName", sortValue: "maidenName" },
    { field: "middleName", sortValue: "middleName" },
    { field: "age", sortValue: "age" },
    { field: "gender", sortValue: "gender.text" },
    { field: "culturalAffiliation", sortValue: "culturalAffiliation.text" },
    { field: "religiousAffiliation", sortValue: "religiousAffiliation.text" },
    { field: "sexualOrientation", sortValue: "sexualOrientation.text" },
    { field: "customClientId", sortValue: "customClientId" },
    { field: "linkedInUrl", sortValue: "linkedInUrl" },
    { field: "twitterHandle", sortValue: "twitterHandle" },
    { field: "facebookUrl", sortValue: "facebookUrl" },
    { field: "status", sortValue: "status" },
    { field: "birthday", sortValue: "birthday" },
    { field: "nextBirthday", sortValue: "nextBirthday" },
    { field: "maritalStatus", sortValue: "maritalStatus.text" },
    { field: "subStatus", sortValue: "subStatus.text" },
    { field: "streetAddress", sortValue: "streetAddress" },
    { field: "city", sortValue: "city" },
    { field: "province", sortValue: "province" },
    { field: "zip", sortValue: "zip" },
    { field: "country", sortValue: "country.name" },
    { field: "email", sortValue: "email" },
    { field: "workPhone", sortValue: "workPhone" },
    { field: "homePhone", sortValue: "homePhone" },
    { field: "cellPhone", sortValue: "cellPhone" },
    { field: "culture", sortValue: "culture.text" },
    { field: "timezone", sortValue: "timezone.text" },
    { field: "streetAddress2", sortValue: "streetAddress2" },
    { field: "municipality", sortValue: "municipality" },
    { field: "region1", sortValue: "region1" },
    { field: "region2", sortValue: "region2" },
    { field: "region3", sortValue: "region3" },
    { field: "neighbourhood", sortValue: "neighbourhood" },
    { field: "email2", sortValue: "email2" },
    { field: "internetAddress", sortValue: "internetAddress" },
    { field: "socialSecurityNumber", sortValue: "socialSecurityNumber" },
    { field: "identityNumber", sortValue: "identityNumber" },
    { field: "regionOfBirth", sortValue: "regionOfBirth" },
    { field: "townOfBirth", sortValue: "townOfBirth" },
    { field: "poBox", sortValue: "poBox" },
    { field: "userId", sortValue: "userId" },
    { field: "registeredDisabled", sortValue: "registeredDisabled.text" },
    { field: "disability", sortValue: "disability.text" },
    { field: "veteranStatus", sortValue: "veteranStatus.text" },
    { field: "equalEmploymentOpportunity", sortValue: "equalEmploymentOpportunity.text" },
    { field: "nationality", sortValue: "nationality.text" },
    { field: "countryOfBirth", sortValue: "countryOfBirth.name" },
    { field: "organization", sortValue: "organization.name" },
    { field: "username", sortValue: "username" },
    { field: "preferredPronoun", sortValue: "preferredPronoun" },
    { field: "nextNumberId1", sortValue: "nextNumberId1" }
  ];

  private columnsSubject = new BehaviorSubject<ColumnConfig[]>(this._columns);
  private filtersSubject = new BehaviorSubject<FilterConfig[]>(this._filters.sort((a, b) => a.field.localeCompare(b.field)));
  private sortsSubject = new BehaviorSubject<SortConfig[]>(this._sorts);

  public columns$: Observable<ColumnConfig[]> = this.columnsSubject.asObservable();
  public filters$: Observable<FilterConfig[]> = this.filtersSubject.asObservable();
  public sorts$: Observable<SortConfig[]> = this.sortsSubject.asObservable();

  constructor(private translate: TranslateService) {}

  getColumnByField(field: string): ColumnConfig | undefined {
    return this._columns.find(col => col.field === field);
  }

  getFilterByField(field: string): FilterConfig | undefined {
    return this._filters.find(filter => filter.field === field);
  }

  getColumnsWithPreferences(userPreferences?: any): ColumnConfig[] {
    if (!userPreferences) return this._columns;

    return this._columns.map(col => ({
      ...col,
      hidden: userPreferences.hiddenColumns?.includes(col.field) || false,
      width: userPreferences.columnWidths?.[col.field] || col.width
    }));
  }
}